import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, X } from 'lucide-react';
import 'pannellum';
import 'pannellum/build/pannellum.css';
import { cn } from '@/utils/cn';
import { chatLucideIconProps } from './chat-ui';
import { Button } from '../ui/button';

export type WebsiteFilePreviewKind =
  | 'image'
  | 'panorama'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'unsupported';

export type WebsiteFilePreviewItem = {
  title: string;
  url: string;
  fileName?: string;
  filePath?: string;
  mimeType?: string;
  generationPrompt?: string;
  kind?: WebsiteFilePreviewKind;
};

const imageExtensions = new Set(['avif', 'gif', 'jpeg', 'jpg', 'png', 'webp']);
const videoExtensions = new Set(['m4v', 'mov', 'mp4', 'webm']);
const audioExtensions = new Set(['aac', 'flac', 'm4a', 'mp3', 'ogg', 'wav']);

function extensionForName(value: string | undefined) {
  const name = value?.trim();
  if (!name) {
    return '';
  }
  const pathSegments = name.split(/[\\/]/);
  let lastSegment = name;
  for (let index = pathSegments.length - 1; index >= 0; index -= 1) {
    if (pathSegments[index]?.length) {
      lastSegment = pathSegments[index];
      break;
    }
  }
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === lastSegment.length - 1) {
    return '';
  }
  return lastSegment.slice(dotIndex + 1).toLowerCase();
}

export function inferWebsiteFilePreviewKind(params: {
  fileName?: string;
  filePath?: string;
  mimeType?: string;
}): WebsiteFilePreviewKind {
  const mimeType = params.mimeType?.trim().toLowerCase() ?? '';
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  if (mimeType.startsWith('video/')) {
    return 'video';
  }
  if (mimeType.startsWith('audio/')) {
    return 'audio';
  }
  if (mimeType === 'application/pdf') {
    return 'pdf';
  }

  const extension =
    extensionForName(params.fileName) || extensionForName(params.filePath);
  if (imageExtensions.has(extension)) {
    return 'image';
  }
  if (videoExtensions.has(extension)) {
    return 'video';
  }
  if (audioExtensions.has(extension)) {
    return 'audio';
  }
  if (extension === 'pdf') {
    return 'pdf';
  }

  return 'unsupported';
}

function isLikelyTwoToOneImage(width: number, height: number): boolean {
  if (width <= 0 || height <= 0) {
    return false;
  }

  return Math.abs(width / height - 2) <= 0.01;
}

function hasPanoramaKeyword(...values: Array<string | undefined>): boolean {
  return values.some(value =>
    /(?:360|equirectangular|pano|panorama|全景)/i.test(value ?? '')
  );
}

export function canPreviewImageAsPanorama(params: {
  width: number;
  height: number;
  fileName?: string;
  filePath?: string;
  generationPrompt?: string;
}): boolean {
  return (
    isLikelyTwoToOneImage(params.width, params.height) &&
    hasPanoramaKeyword(
      params.generationPrompt,
      params.fileName,
      params.filePath
    )
  );
}

function objectURLFromDataURL(dataURL: string): string | null {
  const separatorIndex = dataURL.indexOf(',');
  if (separatorIndex < 0) {
    return null;
  }

  const metadata = dataURL.slice(0, separatorIndex);
  const payload = dataURL.slice(separatorIndex + 1);
  const mimeTypeMatch = metadata.match(/^data:([^;]+)/i);
  const mimeType = mimeTypeMatch?.[1] ?? 'application/octet-stream';
  const isBase64 = /;base64/i.test(metadata);

  try {
    const binary = isBase64
      ? window.atob(payload)
      : decodeURIComponent(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return window.URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  } catch {
    return null;
  }
}

function PanoramaPreview({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.pannellum?.viewer) {
      return;
    }

    const objectURL = url.startsWith('data:')
      ? objectURLFromDataURL(url)
      : null;
    const panorama = objectURL ?? url;
    const viewer = window.pannellum.viewer(container, {
      type: 'equirectangular',
      panorama,
      autoLoad: true,
      showControls: true,
      showZoomCtrl: true,
      showFullscreenCtrl: false,
      mouseZoom: true,
      draggable: true,
      hfov: 95,
      minHfov: 45,
      maxHfov: 120,
      haov: 360,
      vaov: 180,
      ignoreGPanoXMP: true,
      strings: {
        loadingLabel: 'Loading 360° preview...',
        genericWebGLError: 'Unable to load the 360° preview.',
        textureSizeError: 'This panorama is too large to preview here.',
      },
    });

    return () => {
      viewer.destroy();
      if (objectURL) {
        window.URL.revokeObjectURL(objectURL);
      }
    };
  }, [url]);

  return <div ref={containerRef} className="h-full w-full bg-black" />;
}

function ImagePreview({
  preview,
  onPromotePanorama,
}: {
  preview: WebsiteFilePreviewItem;
  onPromotePanorama: () => void;
}) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-black">
      <img
        src={preview.url}
        alt={preview.fileName ?? preview.title}
        className="max-h-full max-w-full object-contain"
        onLoad={event => {
          const image = event.currentTarget;
          if (
            canPreviewImageAsPanorama({
              width: image.naturalWidth,
              height: image.naturalHeight,
              fileName: preview.fileName,
              filePath: preview.filePath,
              generationPrompt: preview.generationPrompt,
            })
          ) {
            onPromotePanorama();
          }
        }}
      />
    </div>
  );
}

function PreviewBody({
  preview,
  kind,
  onPromotePanorama,
}: {
  preview: WebsiteFilePreviewItem;
  kind: WebsiteFilePreviewKind;
  onPromotePanorama: () => void;
}) {
  switch (kind) {
    case 'panorama':
      return <PanoramaPreview url={preview.url} />;
    case 'image':
      return (
        <ImagePreview preview={preview} onPromotePanorama={onPromotePanorama} />
      );
    case 'video':
      return (
        <video
          className="h-full w-full bg-black object-contain"
          src={preview.url}
          controls
          playsInline
          preload="metadata"
        />
      );
    case 'audio':
      return (
        <div className="flex h-full w-full items-center justify-center bg-black px-8">
          <audio className="w-full max-w-2xl" src={preview.url} controls />
        </div>
      );
    case 'pdf':
      return (
        <iframe
          className="h-full w-full border-0 bg-white"
          src={preview.url}
          title={preview.title}
        />
      );
    default:
      return (
        <div className="flex h-full w-full items-center justify-center bg-black px-8 text-center text-sm text-white/70">
          Preview is not available for this file.
        </div>
      );
  }
}

export function ChatFilePreviewModal({
  preview,
  onClose,
  onDownload,
}: {
  preview: WebsiteFilePreviewItem | null;
  onClose: () => void;
  onDownload?: (preview: WebsiteFilePreviewItem) => Promise<void> | void;
}) {
  const initialKind = useMemo(() => {
    if (!preview) {
      return 'unsupported' as WebsiteFilePreviewKind;
    }
    return (
      preview.kind ??
      inferWebsiteFilePreviewKind({
        fileName: preview.fileName,
        filePath: preview.filePath,
        mimeType: preview.mimeType,
      })
    );
  }, [preview]);
  const [promotedPanorama, setPromotedPanorama] = useState(false);

  useEffect(() => {
    setPromotedPanorama(false);
  }, [preview?.url]);

  useEffect(() => {
    if (!preview) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, preview]);

  if (!preview) {
    return null;
  }

  const kind: WebsiteFilePreviewKind = promotedPanorama
    ? 'panorama'
    : initialKind;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3 backdrop-blur-md sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={preview.title}
      onClick={onClose}
    >
      <div
        className="relative flex h-full max-h-[900px] w-full max-w-[1200px] flex-col overflow-hidden rounded-2xl border border-white/12 bg-black shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between gap-3 bg-gradient-to-b from-black/70 to-transparent px-3 py-3 text-white sm:px-4">
          <div className="min-w-0">
            <div className="chat-type-body-sm-strong truncate">
              {preview.title}
            </div>
            {kind === 'panorama' ? (
              <div className="chat-type-caption text-white/60">360°</div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {onDownload ? (
              <Button
                variant="ghost"
                size="icon-sm"
                className="rounded-full bg-white/10 text-white shadow-none hover:bg-white/18 hover:text-white"
                onClick={() => {
                  void onDownload(preview);
                }}
                aria-label={`Download ${preview.fileName ?? preview.title}`}
              >
                <Download {...chatLucideIconProps} className="size-4" />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon-sm"
              className="rounded-full bg-white/10 text-white shadow-none hover:bg-white/18 hover:text-white"
              onClick={onClose}
              aria-label="Close preview"
            >
              <X {...chatLucideIconProps} className="size-4" />
            </Button>
          </div>
        </div>

        <div className={cn('min-h-0 flex-1', kind === 'audio' && 'pt-12')}>
          <PreviewBody
            preview={preview}
            kind={kind}
            onPromotePanorama={() => setPromotedPanorama(true)}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
